import { useToast } from '@govinity/ui/hooks/use-toast';
import { useTranslations } from 'next-intl';
import { useCallback, useRef } from 'react';

import { getMachineTranslationNotice } from '@/lib/utils/machine-translation-notice';

const NOTICE_DURATION_MS = 8000;

/**
 * Discloses machine translation when the viewer turns on a translated caption track: a dismissible toast in that
 * language, once per switch to it.
 */
export const useMachineTranslationNotice = () => {
  const { toast } = useToast();
  const t = useTranslations('MeetingLayout');
  const noticedLanguageRef = useRef<string | null>(null);

  return useCallback(
    (captionLanguage: string | null) => {
      const notice = captionLanguage === null ? null : getMachineTranslationNotice(captionLanguage);
      if (notice === null) {
        noticedLanguageRef.current = null;
        return;
      }
      if (notice.language === noticedLanguageRef.current) return;
      noticedLanguageRef.current = notice.language;
      toast({
        description: (
          <span lang={notice.language} dir="auto">
            {notice.text}
          </span>
        ),
        duration: NOTICE_DURATION_MS,
        closeLabel: t('dismissCaptionTranslationNotice'),
        // The shared toast only reveals its close button on hover, which touch screens never get.
        className: '[&_[toast-close]]:opacity-100',
      });
    },
    [t, toast]
  );
};
